import { useEffect, useRef, useState, type RefObject } from "react";
import { useQuizLogic } from "./hooks/useQuizLogic.js";
import { quizQuestions } from "./config/quizConfig.js";
import { QuizLayout } from "./components/QuizLayout.js";
import { QuestionStep } from "./components/QuestionStep.js";
import { ContactStep } from "./components/ContactStep.js";

export interface PublicFormProps {
  navigateToResult?: (url: string) => void;
  publicConfig?: PublicRuntimeConfig;
}

export interface PublicRuntimeConfig {
  turnstile: {
    enabled: boolean;
    siteKey: string | null;
  };
}

export const disabledPublicConfig: PublicRuntimeConfig = {
  turnstile: { enabled: false, siteKey: null }
};

function parsePublicRuntimeConfig(value: unknown): PublicRuntimeConfig {
  if (!value || typeof value !== "object" || !("turnstile" in value)) {
    throw new Error("invalid_public_config");
  }
  const turnstile = (value as { turnstile?: unknown }).turnstile;
  if (!turnstile || typeof turnstile !== "object") throw new Error("invalid_public_config");
  const candidate = turnstile as { enabled?: unknown; siteKey?: unknown };
  if (typeof candidate.enabled !== "boolean") throw new Error("invalid_public_config");
  if (candidate.enabled && (typeof candidate.siteKey !== "string" || !candidate.siteKey)) {
    throw new Error("invalid_public_config");
  }
  if (!candidate.enabled && candidate.siteKey !== null) throw new Error("invalid_public_config");
  return {
    turnstile: {
      enabled: candidate.enabled,
      siteKey: candidate.siteKey as string | null
    }
  };
}

export function PublicForm({
  navigateToResult = (url) => window.location.assign(url),
  publicConfig: suppliedConfig
}: PublicFormProps = {}) {
  const [loadedConfig, setLoadedConfig] = useState<PublicRuntimeConfig | null>(suppliedConfig ?? null);
  const [configError, setConfigError] = useState("");
  const publicConfig = suppliedConfig ?? loadedConfig;
  const logic = useQuizLogic(navigateToResult, publicConfig?.turnstile.enabled ?? false);
  const titleRef = useRef<HTMLHeadingElement>(null) as RefObject<HTMLHeadingElement>;
  const contactTitleRef = useRef<HTMLHeadingElement>(null) as RefObject<HTMLHeadingElement>;
  const errorSummaryRef = logic.errorSummaryRef as RefObject<HTMLDivElement>;

  useEffect(() => {
    (logic.currentQuestion ? titleRef : contactTitleRef).current?.focus();
    document.title = logic.currentQuestion ? `Pregunta ${logic.step + 1} de ${quizQuestions.length} | Quiz de escalamiento` : "Tus datos | Quiz de escalamiento";
  }, [logic.step, logic.currentQuestion]);

  useEffect(() => {
    if (suppliedConfig) return;
    const controller = new AbortController();
    fetch("/api/v1/public/config", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("public_config_unavailable");
        return parsePublicRuntimeConfig(await response.json());
      })
      .then(setLoadedConfig)
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === "AbortError") return;
        setConfigError("No pudimos cargar la configuración de seguridad. Recarga la página.");
      });
    return () => controller.abort();
  }, [suppliedConfig]);

  return (
    <QuizLayout
      progress={logic.progress}
      questionCount={quizQuestions.length}
      {...(logic.currentQuestion ? { questionNumber: logic.step + 1 } : {})}
    >
      {logic.currentQuestion && (
        <QuestionStep
          advancing={logic.advancing}
          question={logic.currentQuestion}
          answers={logic.answers}
          step={logic.step}
          titleRef={titleRef}
          onSelect={logic.selectAnswer}
          onBack={logic.goBack}
        />
      )}
      <div hidden={Boolean(logic.currentQuestion)}>
        <ContactStep
          submitting={logic.submitting}
          formError={logic.formError}
          configError={configError}
          configPending={!publicConfig && !configError}
          fieldErrors={logic.fieldErrors}
          onInput={logic.updateContactField}
          onBlur={logic.completeContactField}
          emailSuggestion={logic.emailSuggestion}
          onUseEmailSuggestion={logic.applyEmailSuggestion}
          onKeepEmail={logic.keepEmail}
          titleRef={contactTitleRef}
          errorSummaryRef={errorSummaryRef}
          turnstileSiteKey={publicConfig?.turnstile.enabled ? publicConfig.turnstile.siteKey : null}
          turnstileToken={logic.turnstileToken}
          turnstileResetNonce={logic.turnstileResetNonce}
          onTurnstileTokenChange={logic.setTurnstileToken}
          onSubmit={(event) => {
            void logic.submitContactForm(event);
          }}
          onBack={logic.goBack}
        />
      </div>
    </QuizLayout>
  );
}
